import React,{useState,useRef,useCallback,useEffect} from 'react';
import {api,setRealtimeAuth,supabaseRealtime} from '../lib/api';
import {CHAT_PAGE_SIZE,TERMS_VERSION} from '../lib/constants';
import {getLeagueChatRecipients,getTeamChatRecipients,sendPushNotification} from '../lib/pushNotifications';

export function TeamChatView({teamId,channel,senderType,senderId,senderName,token,ac,AS,canPost,onLoaded}){
  const [messages,setMessages]=useState(null);
  const [hasMore,setHasMore]=useState(false);
  const [loadingMore,setLoadingMore]=useState(false);
  const [body,setBody]=useState("");
  const [sending,setSending]=useState(false);
  const bottomRef=useRef(null);
  const seenIds=useRef(new Set());

  // Initial load: only the most recent page, newest-first from the server then
  // reversed for display — not the entire history every time this view opens.
  useEffect(()=>{
    let cancelled=false;
    seenIds.current=new Set();
    setMessages(null);
    (async()=>{
      const rows=await api.get(token,"team_chat_messages",`team_id=eq.${teamId}&channel=eq.${channel}&order=created_at.desc&limit=${CHAT_PAGE_SIZE}`);
      if(cancelled)return;
      const asc=(rows||[]).slice().reverse();
      asc.forEach(m=>seenIds.current.add(m.id));
      setMessages(asc);
      setHasMore((rows||[]).length===CHAT_PAGE_SIZE);
      if(onLoaded)onLoaded(teamId,channel,asc);
    })();
    return()=>{cancelled=true;};
  },[teamId,channel]);

  // Live updates via Supabase Realtime — replaces the old "refetch everything every
  // 15s" polling. New messages (from anyone, any device) just stream in as they're sent.
  useEffect(()=>{
    setRealtimeAuth(token);
    const ch=supabaseRealtime
      .channel(`chat-${teamId}-${channel}`)
      .on("postgres_changes",{event:"INSERT",schema:"public",table:"team_chat_messages",filter:`team_id=eq.${teamId}`},payload=>{
        const m=payload.new;
        if(m.channel!==channel)return;
        if(seenIds.current.has(m.id))return;
        seenIds.current.add(m.id);
        setMessages(prev=>[...(prev||[]),m]);
      })
      .on("postgres_changes",{event:"DELETE",schema:"public",table:"team_chat_messages",filter:`team_id=eq.${teamId}`},payload=>{
        setMessages(prev=>(prev||[]).filter(m=>m.id!==payload.old.id));
      })
      .subscribe();
    return()=>{supabaseRealtime.removeChannel(ch);};
  },[teamId,channel,token]);

  useEffect(()=>{ if(messages)bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[messages?.length]);

  async function loadEarlier(){
    if(!messages||messages.length===0||loadingMore)return;
    setLoadingMore(true);
    const oldest=messages[0];
    const rows=await api.get(token,"team_chat_messages",`team_id=eq.${teamId}&channel=eq.${channel}&created_at=lt.${oldest.created_at}&order=created_at.desc&limit=${CHAT_PAGE_SIZE}`);
    const asc=(rows||[]).slice().reverse();
    asc.forEach(m=>seenIds.current.add(m.id));
    setMessages(prev=>[...asc,...(prev||[])]);
    setHasMore((rows||[]).length===CHAT_PAGE_SIZE);
    setLoadingMore(false);
  }
  async function send(){
    const trimmed=body.trim();
    if(!trimmed||sending)return;
    setSending(true);
    const created=await api.post(token,"team_chat_messages",[{team_id:teamId,channel,sender_type:senderType,sender_id:senderId,sender_name:senderName,body:trimmed}]);
    if(created&&created[0]){
      seenIds.current.add(created[0].id);
      setMessages(prev=>[...(prev||[]),created[0]]);setBody("");
      getTeamChatRecipients(token,teamId,channel,senderId).then(ids=>sendPushNotification(token,ids,"new_message",`${senderName} in ${channel==="parent"?"Parent":"Player"} Chat`,trimmed.slice(0,120)));
    }
    setSending(false);
  }
  async function deleteMessage(id){
    setMessages(prev=>(prev||[]).filter(m=>m.id!==id));
    await api.del(token,"team_chat_messages",id);
  }
  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",minHeight:0}}>
      <div style={{flex:1,minHeight:0,overflowY:"auto",padding:"12px 4px",display:"flex",flexDirection:"column",gap:10}}>
        {messages===null?(
          <div style={{fontSize:12,color:"var(--c-muted2)",textAlign:"center",padding:20}}>Loading…</div>
        ):messages.length===0?(
          <div style={{fontSize:12,color:"var(--c-muted2)",textAlign:"center",padding:20,fontStyle:"italic"}}>No messages yet.</div>
        ):(<>
        {hasMore&&(
          <button onClick={loadEarlier} disabled={loadingMore} style={{alignSelf:"center",padding:"5px 12px",borderRadius:20,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:10,fontWeight:700,cursor:"pointer",marginBottom:4}}>{loadingMore?"Loading…":"Load earlier messages"}</button>
        )}
        {messages.map(m=>{
          const isMine=m.sender_id===senderId;
          return(
            <div key={m.id} style={{alignSelf:isMine?"flex-end":"flex-start",maxWidth:"78%"}}>
              <div style={{fontSize:9,color:"var(--c-muted)",marginBottom:3,padding:"0 3px",display:"flex",gap:6,justifyContent:isMine?"flex-end":"flex-start"}}>
                <span>{m.sender_name}{m.sender_type==="coach"?" · Coach":""} · {new Date(m.created_at).toLocaleString([], {month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
                {isMine&&<button onClick={()=>deleteMessage(m.id)} title="Delete message" style={{background:"none",border:"none",color:"var(--c-muted2)",cursor:"pointer",fontSize:9,padding:0}}>✕</button>}
              </div>
              <div style={{background:isMine?ac:"var(--c-bg0)",color:isMine?"#fff":"var(--c-text)",borderRadius:12,padding:"8px 12px",fontSize:13,lineHeight:1.4,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.body}</div>
            </div>
          );
        })}
        </>)}
        <div ref={bottomRef}/>
      </div>
      {canPost?(
        <div style={{display:"flex",gap:8,paddingTop:10,borderTop:"1px solid var(--c-border)"}}>
          <textarea value={body} onChange={e=>setBody(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder="Write a message…" rows={1} style={{flex:1,padding:"9px 11px",borderRadius:8,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:13,resize:"none",fontFamily:"inherit"}}/>
          <button onClick={send} disabled={sending||!body.trim()} style={{padding:"0 16px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer",opacity:sending||!body.trim()?0.6:1}}>Send</button>
        </div>
      ):(
        <div style={{paddingTop:10,borderTop:"1px solid var(--c-border)",fontSize:11,color:"var(--c-muted)",textAlign:"center",fontStyle:"italic"}}>📢 This is an announcements channel — only the coach can post here.</div>
      )}
    </div>
  );
}

// ── LeagueChatView ────────────────────────────────────────────────────────────
// Coach-to-coach chat scoped to a league, for coaches whose teams participate
// in it — separate from the team-level parent/player chat. Same pagination +
// Realtime approach as TeamChatView: load the most recent page, "Load earlier
// messages" for history, live updates via a websocket subscription instead of
// polling every 15s, and senders can delete their own message.

export function LeagueChatView({leagueId,senderId,senderName,orgName,token,ac,AS,onLoaded}){
  const [messages,setMessages]=useState(null);
  const [hasMore,setHasMore]=useState(false);
  const [loadingMore,setLoadingMore]=useState(false);
  const [body,setBody]=useState("");
  const [sending,setSending]=useState(false);
  const bottomRef=useRef(null);
  const seenIds=useRef(new Set());

  useEffect(()=>{
    let cancelled=false;
    seenIds.current=new Set();
    setMessages(null);
    (async()=>{
      const rows=await api.get(token,"league_chat_messages",`league_id=eq.${leagueId}&order=created_at.desc&limit=${CHAT_PAGE_SIZE}`);
      if(cancelled)return;
      const asc=(rows||[]).slice().reverse();
      asc.forEach(m=>seenIds.current.add(m.id));
      setMessages(asc);
      setHasMore((rows||[]).length===CHAT_PAGE_SIZE);
      if(onLoaded)onLoaded(leagueId,asc);
    })();
    return()=>{cancelled=true;};
  },[leagueId]);

  useEffect(()=>{
    setRealtimeAuth(token);
    const ch=supabaseRealtime
      .channel(`league-chat-${leagueId}`)
      .on("postgres_changes",{event:"INSERT",schema:"public",table:"league_chat_messages",filter:`league_id=eq.${leagueId}`},payload=>{
        const m=payload.new;
        if(seenIds.current.has(m.id))return;
        seenIds.current.add(m.id);
        setMessages(prev=>[...(prev||[]),m]);
      })
      .on("postgres_changes",{event:"DELETE",schema:"public",table:"league_chat_messages",filter:`league_id=eq.${leagueId}`},payload=>{
        setMessages(prev=>(prev||[]).filter(m=>m.id!==payload.old.id));
      })
      .subscribe();
    return()=>{supabaseRealtime.removeChannel(ch);};
  },[leagueId,token]);

  useEffect(()=>{ if(messages)bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[messages?.length]);

  async function loadEarlier(){
    if(!messages||messages.length===0||loadingMore)return;
    setLoadingMore(true);
    const oldest=messages[0];
    const rows=await api.get(token,"league_chat_messages",`league_id=eq.${leagueId}&created_at=lt.${oldest.created_at}&order=created_at.desc&limit=${CHAT_PAGE_SIZE}`);
    const asc=(rows||[]).slice().reverse();
    asc.forEach(m=>seenIds.current.add(m.id));
    setMessages(prev=>[...asc,...(prev||[])]);
    setHasMore((rows||[]).length===CHAT_PAGE_SIZE);
    setLoadingMore(false);
  }
  async function send(){
    const trimmed=body.trim();
    if(!trimmed||sending)return;
    setSending(true);
    const created=await api.post(token,"league_chat_messages",[{league_id:leagueId,sender_id:senderId,sender_name:senderName,org_name:orgName,body:trimmed}]);
    if(created&&created[0]){
      seenIds.current.add(created[0].id);
      setMessages(prev=>[...(prev||[]),created[0]]);setBody("");
      getLeagueChatRecipients(token,leagueId,senderId).then(ids=>sendPushNotification(token,ids,"new_message",`${senderName}${orgName?` (${orgName})`:""} in League Chat`,trimmed.slice(0,120)));
    }
    setSending(false);
  }
  async function deleteMessage(id){
    setMessages(prev=>(prev||[]).filter(m=>m.id!==id));
    await api.del(token,"league_chat_messages",id);
  }
  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",minHeight:0}}>
      <div style={{flex:1,minHeight:0,overflowY:"auto",padding:"12px 4px",display:"flex",flexDirection:"column",gap:10}}>
        {messages===null?(
          <div style={{fontSize:12,color:"var(--c-muted2)",textAlign:"center",padding:20}}>Loading…</div>
        ):(<>
          {messages.length>0&&(hasMore?(
            <button onClick={loadEarlier} disabled={loadingMore} style={{alignSelf:"center",padding:"6px 14px",borderRadius:20,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-muted)",fontSize:11,fontWeight:700,cursor:"pointer",opacity:loadingMore?0.6:1,marginBottom:4}}>{loadingMore?"Loading…":"Load earlier messages"}</button>
          ):(
            <div style={{fontSize:10,color:"var(--c-muted2)",textAlign:"center",padding:"2px 0 6px"}}>Start of conversation</div>
          ))}
          {messages.length===0?(
            <div style={{fontSize:12,color:"var(--c-muted2)",textAlign:"center",padding:20,fontStyle:"italic"}}>No messages yet.</div>
          ):messages.map(m=>{
            const isMine=m.sender_id===senderId;
            return(
              <div key={m.id} style={{alignSelf:isMine?"flex-end":"flex-start",maxWidth:"78%"}}>
                <div style={{fontSize:9,color:"var(--c-muted)",marginBottom:3,padding:"0 3px",display:"flex",gap:6,alignItems:"center",justifyContent:isMine?"flex-end":"flex-start"}}>
                  <span>{m.sender_name}{m.org_name?` · ${m.org_name}`:""} · {new Date(m.created_at).toLocaleString([], {month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
                  {isMine&&<button onClick={()=>deleteMessage(m.id)} title="Delete message" style={{border:"none",background:"none",color:"var(--c-muted2)",cursor:"pointer",fontSize:11,padding:0,lineHeight:1}}>✕</button>}
                </div>
                <div style={{background:isMine?ac:"var(--c-bg0)",color:isMine?"#fff":"var(--c-text)",borderRadius:12,padding:"8px 12px",fontSize:13,lineHeight:1.4,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.body}</div>
              </div>
            );
          })}
        </>)}
        <div ref={bottomRef}/>
      </div>
      <div style={{display:"flex",gap:8,paddingTop:10,borderTop:"1px solid var(--c-border)"}}>
        <textarea value={body} onChange={e=>setBody(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder="Write a message…" rows={1} style={{flex:1,padding:"9px 11px",borderRadius:8,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:13,resize:"none",fontFamily:"inherit"}}/>
        <button onClick={send} disabled={sending||!body.trim()} style={{padding:"0 16px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer",opacity:sending||!body.trim()?0.6:1}}>Send</button>
      </div>
    </div>
  );
}

// ── Terms & Conditions / Privacy Policy ─────────────────────────────────────
// IMPORTANT: this is a general-purpose starting template, not legal advice. It
// has not been reviewed by a qualified lawyer for your specific club/region —
// have one review and adapt it (especially the governing-law and children's
// data sections) before relying on it. Bump TERMS_VERSION whenever the text
// changes materially so existing users can be asked to re-accept.

